import { sendClientError } from '@/lib/clientLogger';

function getErrorTargetContext(target: EventTarget | null) {
  if (!target || target === window) {
    return null;
  }

  if (target instanceof HTMLScriptElement) {
    return {
      tagName: target.tagName,
      source: target.src || null,
      crossOrigin: target.crossOrigin || null,
      async: target.async,
      defer: target.defer,
    };
  }

  if (target instanceof HTMLImageElement) {
    return {
      tagName: target.tagName,
      source: target.currentSrc || target.src || null,
      crossOrigin: target.crossOrigin || null,
      alt: target.alt || null,
    };
  }

  if (target instanceof HTMLLinkElement) {
    return {
      tagName: target.tagName,
      source: target.href || null,
      rel: target.rel || null,
      as: target.as || null,
      crossOrigin: target.crossOrigin || null,
    };
  }

  if (target instanceof Element) {
    return {
      tagName: target.tagName,
      id: target.id || null,
      className: typeof target.className === 'string' ? target.className : null,
    };
  }

  return null;
}

function getPageContext(event: Event) {
  return {
    isTrusted: event.isTrusted,
    target: getErrorTargetContext(event.target),
    pathname: window.location.pathname,
    search: window.location.search,
    referrer: document.referrer || null,
    visibilityState: document.visibilityState,
  };
}

window.addEventListener('error', (event) => {
  const targetContext = getErrorTargetContext(event.target);

  if (!(event instanceof ErrorEvent)) {
    sendClientError({
      type: 'resource.error',
      message: targetContext?.source ? `Resource load error: ${targetContext.source}` : 'Resource load error',
      stack: null,
      source: targetContext?.source ?? null,
      lineno: null,
      colno: null,
      context: getPageContext(event),
    });

    return;
  }

  sendClientError({
    type: 'window.error',
    message: event.message,
    stack: event.error?.stack ?? null,
    source: event.filename || null,
    lineno: event.lineno || null,
    colno: event.colno || null,
    context: {
      ...getPageContext(event),
      errorName: event.error?.name ?? null,
    },
  });
}, true)

window.addEventListener('unhandledrejection', (event) => {
  sendClientError({
    type: 'unhandledrejection',
    message: event.reason?.message ?? String(event.reason),
    stack: event.reason?.stack ?? null,
    lineno: null,
    colno: null,
    context: {
      pathname: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer || null,
      visibilityState: document.visibilityState,
      reasonType: event.reason?.constructor?.name ?? typeof event.reason,
    },
  });
})
